import { h } from 'preact';
import { useState, useRef, useEffect } from 'preact/hooks';
import Panel from 'utils/panel';

import { IconClose, IconBag } from 'utils/icon';

import {
  formatMoney,
  formatCartLinesAjax,
} from 'utils/storefront/format-resource';
import ProgressBarThreshold from './ProgressBarThreshold'; // Import the component
import GwpDropdown from './GwpDropdown'; // Import the component
import ProductComponent from '../ProductComponent';
import { withCartContext } from './cart-context';
import CartItem from './cart-item';

const Cart = (props) => {
  const { cart } = props;
  const {
    data: { emptyStateProducts, emptyStateProductsTitle, sectionId, gwpData },
  } = props;
  const closeTriggerEl = useRef(null);
  const isCartRedirect = new URLSearchParams(window.location.search).get(
    'cart',
  );
  const [panelTriggers, setPanelTriggers] = useState({
    openTriggerEl: document.querySelector('[data-header-cart-trigger]'),
    closeTriggerEl: null,
  });
  // const [tierSubtotal, setTierSubtotal] = useState(cart?.state?.total_price);
  const [currentTier, setCurrentTier] = useState(null);

  const [upsellProduct, setUpsellProduct] = useState();
  const [gwpLine, setGwpLine] = useState(null);
  const [dropdownLoading, setDropdownLoading] = useState(false);

  const filterAndSortGwpData = (array) => {
    // Step 1: Remove objects with null threshold
    const step1Result = array.filter((item) => item.threshold !== null);

    // Step 2: Remove objects with null product and shipping_threshold is false
    const step2Result = step1Result.filter(
      (item) => item.product !== null || item.shipping_threshold === true,
    );

    // Step 3: Remove objects with product not available
    const step3Result = step2Result.filter(
      (item) => item.product === null || item.product.available === true,
    );

    // Step 4: Order the array by "threshold" value
    const finalResult = step3Result.sort((a, b) => a.threshold - b.threshold);

    return finalResult;
  };

  const containsHandle = (array, handle) =>
    array.some((item) => item.handle === handle);

  const gwpFilteredData = filterAndSortGwpData(gwpData);

  useEffect(() => {
    if (!closeTriggerEl.current) return;
    setPanelTriggers((value) => ({
      ...value,
      closeTriggerEl: closeTriggerEl.current,
    }));
  }, [closeTriggerEl]);

  useEffect(() => {
    const cartItems = cart?.state?.items;
    const upsellProductData = localStorage.getItem('upsellProductData');
    let upsellData = upsellProductData ? JSON.parse(upsellProductData) : null;

    if (cartItems && upsellData?.handle) {
      const upsellAlreadyInCart = containsHandle(cartItems, upsellData.handle);
      if (upsellAlreadyInCart) {
        upsellData = null;
      }
    }

    setUpsellProduct(upsellData);
  }, [cart?.state?.items]);

  const lines =
    cart?.mode === 'storefront'
      ? cart?.state?.lines
      : formatCartLinesAjax(cart?.state?.items);

  useEffect(() => {
    // eslint-disable-next-line no-unused-vars
    let total = cart?.state?.total_price;
    gwpFilteredData.forEach((item) => {
      const matchProduct = lines.find(
        (line) => line.handle === item.product?.handle,
      );
      if (matchProduct) total -= matchProduct.final_line_price;
    });
    // setTierSubtotal(total);
  }, [cart?.state?.total_price, gwpFilteredData, lines]);

  useEffect(() => {
    if (!lines?.length) return;
    const gwpItem = lines.find((line) => line.properties?._gwp);
    if (gwpItem) setGwpLine(gwpItem);
    else setGwpLine(null);
    setDropdownLoading(false);
  }, [lines]);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    const gwpLineItem = lines.find((line) => line.properties?._gwp);
    if (gwpLineItem) {
      const gwpLineItemData = gwpFilteredData.find(
        (data) => data.product?.id === gwpLineItem.product_id,
      );

      if (currentTier === false) {
        const payload = [gwpLineItem.key];
        return cart.remove(payload);
      }
      if (gwpLineItemData?.threshold > currentTier?.threshold) {
        const payload = [gwpLineItem.key];
        return cart.remove(payload);
      }
    }
  }, [currentTier, gwpFilteredData]);

  const gwpPrice = gwpLine?.discounted_price || 0;
  const totalPrice = cart?.state?.total_price ?? 0;
  const finalPrice = (totalPrice - (gwpPrice || 0)) / 100;

  return (
    <Panel
      triggers={panelTriggers}
      defaultOpen={isCartRedirect}
      context="cart"
      overlay={true}
    >
      <div className="mini-cart-height relative">
        <div className="absolute inset-x-0 top-0 z-3 flex justify-between border-b border-gray-600 bg-white px-20 py-16 md:py-32">
          <div>
            <h4 className="flex items-center gap-8">
              <IconBag className={'h-20 w-auto'} />
              {window.TMW.Strings.cart.title} ({cart?.state?.item_count})
            </h4>
          </div>
          <button
            type="button"
            ref={closeTriggerEl}
          >
            <IconClose className={'h-[22.17px] w-[22.17px]'} />
          </button>
        </div>
        <div
          className="h-screen overflow-y-auto pb-[245px] pt-56 lg:pt-90"
          data-parent-scroll
        >
          {(cart?.state?.items || []).length > 0 ? (
            <div>
              <ProgressBarThreshold
                cartSubtotal={finalPrice}
                thresholdData={gwpFilteredData}
                setCurrentTier={setCurrentTier}
              />
              {currentTier && !currentTier.shipping_threshold && (
                <GwpDropdown
                  data={currentTier}
                  sectionId={sectionId}
                  gwpLine={gwpLine}
                  cart={cart}
                  loading={dropdownLoading}
                  setLoading={setDropdownLoading}
                />
              )}
              <div className="flex flex-col flex-wrap border-t border-gray-600 pt-16">
                {lines.map((line, index) => (
                  <CartItem
                    key={index}
                    index={index}
                    line={line}
                  />
                ))}

                {upsellProduct !== null && (
                  <div className="order-2 my-20 bg-gray-400 px-20 py-16 group-first-of-type:order-1">
                    <h4 className="mb-16">Goes Well With</h4>
                    <ProductComponent
                      key={upsellProduct.id}
                      product={upsellProduct}
                      sectionId={sectionId}
                      viewDetails="false"
                      quantityInput="false"
                    />
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div>
              <div className="pt-32 text-center lg:pt-40">
                <p className="body-1 mb-8 block">
                  {window.TMW.Strings.cart.empty}
                </p>
                <a
                  href="/"
                  className="button-primary-inverse inline-flex items-center"
                >
                  {window.TMW.Strings.cart.shop_now}
                </a>
              </div>
              {emptyStateProducts.length > 0 ? (
                <div className="absolute inset-0 top-[200px] overflow-y-auto bg-gray-400 px-20 py-24 pb-100 lg:top-[260px]">
                  <h4 className="mb-16 lg:mb-8">{emptyStateProductsTitle}</h4>
                  {emptyStateProducts.map((product, index) =>
                    product.available ? (
                      <ProductComponent
                        key={index}
                        product={product}
                        sectionId={sectionId}
                        viewDetails="false"
                        quantityInput="false"
                      />
                    ) : null,
                  )}
                </div>
              ) : null}
            </div>
          )}
        </div>
        {(cart?.state?.items || []).length > 0 ? (
          <div className="absolute inset-x-0 bottom-0 min-h-[168px] items-center border-t border-gray-600 bg-white px-20 py-16">
            <div className="flex justify-between">
              <div className="eyebrow">{window.TMW.Strings.cart.total}</div>
              <div className="body-2 items-center">
                {cart.mode === 'storefront'
                  ? formatMoney(cart?.state?.subtotal || { amount: 0 })
                  : formatMoney(
                      finalPrice ? { amount: finalPrice * 100 } : { amount: 0 },
                      true,
                    )}
              </div>
            </div>
            <div className="my-16">
              <a
                className="button button-primary-inverse mb-8 block w-full text-center leading-150"
                href="/checkout"
              >
                {cart.mode === 'storefront'
                  ? cart?.state?.checkoutUrl
                  : window.TMW.Strings.cart.checkout}
              </a>
              <span className="body-3 block text-center text-gray-800">
                {window.TMW.Strings.cart.calculated}
              </span>
            </div>
            <div className="text-center">
              <a
                href="/"
                className="button-secondary inline-block"
              >
                {window.TMW.Strings.cart.shop_now}
              </a>
            </div>
          </div>
        ) : null}
      </div>
    </Panel>
  );
};

export default withCartContext(Cart);
